import api from '@/lib/api';
import type { PageResponse } from '@/types/admin.model';
import { toPageResponse } from '@/types/admin.model';
import type { DebtPaymentStatus } from '@/types/manager.model';

const BASE = '/payments/debt-payments';

// Runtime list for the status filter's <option> elements. Typed against the
// existing domain enum below rather than defining its own, so the two can't
// drift apart.
export const DEBT_REQUEST_STATUSES: readonly DebtPaymentStatus[] = [
  'PENDING_REVIEW',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
];

/** Same status enum as the owner-facing debt cancellation flow. */
export type DebtRequestStatus = DebtPaymentStatus;

/** Who submitted the request. The admin projection is fixed server-side. */
export interface DebtRequestUser {
  pk: string;
  email: string;
  name: string;
}

export interface AdminDebtRequest {
  id: string;
  user: DebtRequestUser | null;
  amount: number;
  transferDate: string;
  notes: string | null;
  status: DebtRequestStatus;
  createdAt: string | null;
  reviewedAt: string | null;
  reviewedBy: DebtRequestUser | null;
  reviewNotes: string | null;
}

interface ListParams {
  status: DebtRequestStatus;
  page: number;
  size: number;
}

export const adminDebtPaymentsApi = {
  list: async ({
    status,
    page,
    size,
  }: ListParams): Promise<PageResponse<AdminDebtRequest>> => {
    const response = await api.get(BASE, { params: { status, page, size } });
    return toPageResponse<AdminDebtRequest>(response.data);
  },

  getById: async (id: string): Promise<AdminDebtRequest> => {
    const response = await api.get(`${BASE}/${id}`);
    return response.data;
  },

  /**
   * Short-lived signed URL for the receipt. The `receiptUrl` on the DTO is the
   * raw S3 URL and may not be reachable, so it is never rendered directly.
   */
  receiptUrl: async (id: string): Promise<string> => {
    const response = await api.get(`${BASE}/${id}/receipt`);
    return response.data.url;
  },

  approve: async (id: string, reviewNotes?: string): Promise<AdminDebtRequest> => {
    const response = await api.post(`${BASE}/${id}/approve`, { reviewNotes });
    return response.data;
  },

  reject: async (id: string, reviewNotes: string): Promise<AdminDebtRequest> => {
    const response = await api.post(`${BASE}/${id}/reject`, { reviewNotes });
    return response.data;
  },
};
